import { createContext, useMemo } from "react"
import { Outlet } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../state/hooks"
import { useLazyQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { setSites, setSite, getSites } from "../state/slices/sitesSlice"

type ContextState = {
  getSites: () => void
  getSite: (id: string) => void
  loading: boolean
}

export const SitesContext = createContext<ContextState | undefined>(undefined)

export const SitesProvider = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useAppDispatch()

  const [fetchSites, { loading: loadingSites }] = useLazyQuery(gqlTypes.GetSitesWithLocationDocument, {})
  const [fetchSite, { loading: loadingSite }] = useLazyQuery(gqlTypes.GetSiteWithLocationDocument, {})
  const sites = useAppSelector(getSites)

  const value = useMemo(
    () => ({
      getSites: () => {
        fetchSites().then(({ data }) => {
          if (data?.sites) dispatch(setSites(data.sites))
        })
      },
      getSite: (id: string) => {
        const site = sites.find((s) => s.id === id)
        if (site) return site
        fetchSite({
          variables: {
            id,
          },
        }).then(({ data }) => {
          if (data?.site) dispatch(setSite(data.site))
        })
      },
      loading: loadingSites || loadingSite,
    }),
    [dispatch, fetchSite, fetchSites, loadingSite, loadingSites, sites],
  )

  return <SitesContext.Provider value={value}>{children ? children : <Outlet />}</SitesContext.Provider>
}
